import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "../css/homeDashboard.css"; // Mantém o mesmo CSS limpo que criamos

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR").format(value || 0);

// --- CONFIGURAÇÃO DE CORES POR SECRETARIA ---
const SECRETARIA_CONFIG = [
  {
    label: "Agricultura",
    key: "02.13 - Secretaria Municipal da Agricultura",
    color: "#2ecc70d0",
  }, // Verde
  {
    label: "Obras",
    key: "02.07 - Secretaria Municipal de Obras e Serviços Municipais",
    color: "#e67d22c4",
  }, // Laranja
  {
    label: "Governo",
    key: "02.15 - Secretaria Municipal de Governo e Gestão Estratégica",
    color: "#34495ea9",
  }, // Cinza Azulado
  {
    label: "Meio Ambiente",
    key: "02.17 - Secretaria Municipal de Meio Ambiente",
    color: "#27ae5fa1",
  }, // Verde Escuro
  { label: "SAEG", key: "SAEG", color: "#3498dba8" }, // Azul Água
  { label: "CODESG", key: "CODESG", color: "#9c59b6b9" }, // Roxo
  {
    label: "Planejamento",
    key: "02.03 - Secretaria Municipal de Planejamento, Coordenação e Habitação",
    color: "#2c3e50bb",
  }, // Azul Escuro
  {
    label: "Saúde",
    key: "02.11 - Secretaria Municipal de Saúde",
    color: "#1abc9cb2",
  }, // Turquesa
  {
    label: "Educação",
    key: "02.09 - Secretaria Municipal da Educação",
    color: "#f1c40fbd",
  }, // Amarelo
  {
    label: "Segurança",
    key: "02.08 - Secretaria Municipal de Segurança e Mobilidade Urbana",
    color: "#c03a2bb2",
  }, // Vermelho Escuro
  {
    label: "Turismo",
    key: "02.11 - Secretaria Municipal de Turismo e Lazer",
    color: "#e91e62c0",
  }, // Rosa
  {
    label: "Esporte",
    key: "02.10 - Secretaria Municpal de Esportes",
    color: "#8d44adb9",
  }, // Violeta
  {
    label: "Cultura",
    key: "02.16 - Secretaria Municipal da Cultura",
    color: "#d35400b6",
  }, // Abóbora
  {
    label: "Mulher",
    key: "02.20 -Secretaria de Políticas para as Mulheres",
    color: "#ff6b81b9",
  }, // Rosa Claro
  {
    label: "Defesa Civil", 
    key: "Assessoria Especial de Defesa Civil",
    color: "#e74d3ccb",
  }, // Vermelho Alerta
  {
    label: "Administração",
    key: "02.05 - Secretaria Municipal da Administração",
    color: "#16a084b9",
  }, // Verde Mar
];

function Home() {
  const [kpis, setKpis] = useState({ total: 0, valorTotal: 0, maiorValor: 0 });
  const [dadosQuantidade, setDadosQuantidade] = useState({});
  const [dadosValor, setDadosValor] = useState({});
  const [viewType, setViewType] = useState("quantidade");

  useEffect(() => {

    axios
      .get("/api/totalprojeto")
      .then((res) => setKpis((p) => ({ ...p, total: res.data[0]?.total || 0 })))
      .catch(console.error);
    axios
      .get("/api/valorprojeto")
      .then((res) =>
        setKpis((p) => ({ ...p, valorTotal: res.data[0]?.valor || 0 })),
      )
      .catch(console.error);
    axios
      .get("/api/maiorvalorprojeto")
      .then((res) =>
        setKpis((p) => ({ ...p, maiorValor: res.data[0]?.maior || 0 })),
      )
      .catch(console.error);

    axios
      .get("/api/quantidade-por-secretaria")
      .then((res) => {
        const map = {};
        res.data.forEach(({ secretaria, total }) => (map[secretaria] = total));
        setDadosQuantidade(map);
      })
      .catch(console.error);

    axios
      .get("/api/valores-por-secretaria")
      .then((res) => {
        const map = {};
        res.data.forEach(
          ({ secretaria, total_valor }) =>
            (map[secretaria] = Number(total_valor)),
        );
        setDadosValor(map);
      })
      .catch(console.error);
  }, []);

  const getChartData = () => {
    const sourceData = viewType === "quantidade" ? dadosQuantidade : dadosValor;
    const values = SECRETARIA_CONFIG.map((i) => sourceData[i.key] || 0);
    // Mapeia as cores definidas na configuração
    const colors = SECRETARIA_CONFIG.map((i) => i.color);

    return {
      labels: SECRETARIA_CONFIG.map((i) => i.label),
      datasets: [
        {
          label: viewType === "quantidade" ? "Processos" : "Valor",
          data: values,
          backgroundColor: colors, // Aplica as cores individuais
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
        },
      ],
    };
  };

  const getChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 14 },
        callbacks: {
          label: (ctx) =>
            viewType === "quantidade"
              ? ` ${ctx.raw} Processos`
              : ` ${formatCurrency(ctx.raw)}`,
          labelColor: function (context) {
            return {
              borderColor: context.dataset.backgroundColor[context.dataIndex],
              backgroundColor:
                context.dataset.backgroundColor[context.dataIndex],
            };
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: {
          color: "#334155", // Cor um pouco mais escura para destacar o bold
          font: {
            size: 13, // AUMENTEI O TAMANHO (era 11)
            weight: "bold", // ADICIONEI O NEGRITO
          },
          callback: (val) =>
            viewType === "quantidade" ? val : (val / 1000000).toFixed(0) + "M",
        },
      },
      x: {
        grid: { display: false },
        ticks: {
          color: "#334155", // Cor um pouco mais escura para destacar o bold
          font: {
            size: 12, // AUMENTEI O TAMANHO (era 10)
            weight: "bold", // ADICIONEI O NEGRITO
          },
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  });

  return (
    <div className="dashboard-container">
      <header className="dashboard-heade">
        <div className="brand-area">
          <h4>DASHBOARD GERAL</h4>
          <span>Visão Estratégica</span>
        </div>
        
      </header>

      <div className="dashboard-content">
        <div className="kpi-grid">
          <div className="kpi-card border-blue">
            <div className="card-header">
              <span className="card-title">Processos Ativos</span>
              <i className="fas fa-layer-group card-icon"></i>
            </div>
            <div className="card-main-value">{formatNumber(kpis.total)}</div>
          </div>

          <div className="kpi-card border-green">
            <div className="card-header">
              <span className="card-title">Investimento Total</span>
              <i className="fas fa-dollar-sign card-icon"></i>
            </div>
            <div
              className="card-main-value"
              style={{
                fontSize: kpis.valorTotal > 100000000 ? "1.6rem" : "2rem",
              }}
            >
              {formatCurrency(kpis.valorTotal)}
            </div>
          </div>

          <div className="kpi-card border-orange">
            <div className="card-header">
              <span className="card-title">Secretarias</span>
              <i className="fas fa-building card-icon"></i>
            </div>
            <div className="card-main-value">18</div>
          </div>

          <div className="kpi-card border-red">
            <div className="card-header">
              <span className="card-title">Maior Projeto</span>
              <i className="fas fa-trophy card-icon"></i>
            </div>
            <div className="card-main-value" style={{ fontSize: "1.5rem" }}>
              {formatCurrency(kpis.maiorValor)}
            </div>
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header-row">
            <div className="chart-title-text">
              Análise por Secretaria
              <small
                className="text-muted ms-2 fw-normal"
                style={{ fontSize: "0.8rem" }}
              >
                (Comparativo)
              </small>
            </div>

            <div className="view-switch">
              <button
                className={`switch-btn ${viewType === "quantidade" ? "active" : ""}`}
                onClick={() => setViewType("quantidade")}
              >
                Quantidade
              </button>
              <button
                className={`switch-btn ${viewType === "valor" ? "active" : ""}`}
                onClick={() => setViewType("valor")}
              >
                Valor (R$)
              </button>
            </div>
          </div>

          <div className="chart-container-wrapper">
            <Bar data={getChartData()} options={getChartOptions()} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
